import Vue from "vue";
import Vuex from "vuex";
import Router from "@/router";
import moment from "moment";
import axios from "axios";
import { requestApi } from "@/api";
import { decodeFileError } from "@/utils";

Vue.use(Vuex);

function getRandomInt(min, max) {
  return Math.floor(Math.random() * (max - min)) + min;
}

export default new Vuex.Store({
  getters: {
    isUserAdmin(state) {
      return state.user.role === "admin";
    },
    isUserAdminOrDefault(state) {
      return ["admin", "user"].includes(state.user.role);
    },
    userRole(state) {
      return state.user.role;
    },
    isAuthenticated(state) {
      return !!state.authToken;
    }
  },
  state: {
    messages: [],
    authToken: sessionStorage.getItem("authToken"),
    user: {
      name: sessionStorage.getItem("userName"),
      role: sessionStorage.getItem("userRole")
    },
    orders: {
      items: [],
      totalItems: 0
    },
    users: {
      items: [],
      totalItems: 0
    },
    haspInfo: [],
    labelSearchInfo: {}
  },
  mutations: {
    clearAuth(state) {
      state.authToken = null;
      state.user.name = null;
      state.user.role = null;
      sessionStorage.removeItem("authToken");
      sessionStorage.removeItem("userName");
      sessionStorage.removeItem("userRole");
    },
    setSession(state, { token, name, role }) {
      state.authToken = token;
      state.user.name = name;
      state.user.role = role;
      sessionStorage.setItem("authToken", token);
      sessionStorage.setItem("userName", name);
      sessionStorage.setItem("userRole", role);
    },
    setOrders(state, { items, totalItems }) {
      state.orders.items =
        items.map(i => {
          i.createdAt = moment(i.createdAt * 1000).format("DD.MM.YYYY hh:mm");
          return i;
        }) || [];
      state.orders.totalItems = totalItems;
    },
    setUsers(state, items) {
      state.users.items = items.map(i => {
        switch (i.role) {
          case "admin":
            i.role = "Администратор";
            break;
          case "support":
            i.role = "Support";
            break;
          default:
            i.role = "Пользователь";
        }
        return i;
      });
    },
    setEditOrder(state, order) {
      state.editingOrder = order;
    },
    createMessage(state, message) {
      state.messages.push(message);
    },
    removeMessage(state, id) {
      state.messages = state.messages.filter(i => i.id !== id);
    },
    setHaspInfo(state, info) {
      state.haspInfo = info;
    }
  },
  actions: {
    async addMessage({ commit }, { text, type = "info", timeout = 3000 }) {
      let id = getRandomInt(1, 999999);
      commit("createMessage", { id, text, type, timeout });
      if (timeout > 0) {
        setTimeout(() => {
          commit("removeMessage", id);
        }, timeout);
      }
    },
    async getOrderFile({ state }, id) {
      try {
        const response = await axios.request({
          url: `/api/v0/orders/${id}/file`,
          method: "GET",
          headers: { Authorization: `Bearer ${state.authToken}` },
          responseType: "arraybuffer",
          maxContentLength: Infinity,
          timeout: 3600 * 1000
        });
        return new Blob([response.data], { type: "application/zip" });
      } catch (error) {
        throw decodeFileError(error);
      }
    },
    async deleteUser({ state }, { id }) {
      await requestApi(`/api/v0/users/${id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${state.authToken}` }
      });
    },
    async updateUser({ state }, { id, name, role, login, password }) {
      await requestApi(`/api/v0/users/${id}`, {
        method: "PATCH",
        headers: { Authorization: `Bearer ${state.authToken}` },
        body: JSON.stringify({ name, role, login, password })
      });
    },
    async createUser({ state }, { name, role, login, password }) {
      return requestApi("/api/v0/users", {
        method: "POST",
        headers: { Authorization: `Bearer ${state.authToken}` },
        body: JSON.stringify({ name, role, login, password })
      });
    },
    async getUser({ state }, id) {
      return requestApi(`/api/v0/users/${id}`, {
        method: "GET",
        headers: { Authorization: `Bearer ${state.authToken}` }
      });
    },
    async getLabelInfo({ state }, code) {
      const query = new URLSearchParams({ code });
      return requestApi(`/api/v0/labels/search?${query}`, {
        method: "GET",
        headers: { Authorization: `Bearer ${state.authToken}` }
      });
    },
    async updateOrder({ state }, { id, description }) {
      await requestApi(`/api/v0/orders/${id}`, {
        method: "PATCH",
        headers: { Authorization: `Bearer ${state.authToken}` },
        body: JSON.stringify({ description })
      });
    },
    async createNewOrder({ state }, { labelCount, description }) {
      return requestApi("/api/v0/orders", {
        method: "POST",
        headers: { Authorization: `Bearer ${state.authToken}` },
        body: JSON.stringify({ labelCount: +labelCount, description })
      });
    },
    async getOrder({ state }, orderId) {
      return requestApi(`/api/v0/orders/${orderId}`, {
        method: "GET",
        headers: { Authorization: `Bearer ${state.authToken}` }
      });
    },
    async getOrderShort({ state }, orderId) {
      return requestApi(`/api/v0/orders/${orderId}/short`, {
        method: "GET",
        headers: { Authorization: `Bearer ${state.authToken}` }
      });
    },
    async getHaspInfo({ commit, state }) {
      const info = await requestApi("/api/v0/hasp/info", {
        method: "GET",
        headers: { Authorization: `Bearer ${state.authToken}` }
      });
      commit("setHaspInfo", info);
    },
    async getOrders({ commit, state }, { page, size, sort, order }) {
      const query = new URLSearchParams({ page, size, sort, order });
      const result = await requestApi(`/api/v0/orders?${query}`, {
        method: "GET",
        headers: { Authorization: `Bearer ${state.authToken}` }
      });
      result.items = result.items.map(order => {
        const date = new Date(order.createdAt * 1000);
        order.serial =
          date.getFullYear().toString() +
          (date.getMonth() + 1).toString() +
          order.id.toString().padStart(6, "0");
        return order;
      });
      commit("setOrders", result);
    },
    async getUsers({ commit, state }) {
      const users = await requestApi("/api/v0/users", {
        method: "GET",
        headers: { Authorization: `Bearer ${state.authToken}` }
      });
      commit("setUsers", users);
    },
    async login({ commit }, { login, password }) {
      const query = new URLSearchParams({
        login,
        password: password.b64encode()
      });
      const { token } = await requestApi(`/api/v0/login?${query}`, {
        method: "POST"
      });
      const user = await requestApi("/api/v0/users/me", {
        method: "GET",
        headers: { Authorization: `Bearer ${token}` }
      });
      commit("setSession", { token, ...user });
      if (user.role === "support") {
        Router.push({ name: "LabelSearchPage" });
      } else {
        Router.push({ name: "OrdersPage" });
      }
      return true;
    },
    async logout({ commit }) {
      await commit("clearAuth");
      Router.push({ name: "LoginPage" });
    }
  },
  modules: {}
});
